//************************************************************************************************
// TransactionDetailsModal
//
// Reusable Tier 1 view: displays a read-only summary of the currently selected transaction.
// Used by SupportFiscalReport.jsx whenever the user requests details for a selected row.
//************************************************************************************************

import React from 'react';
import { useState } from 'react';
import {
	Button,
	ButtonGroup,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	IconButton,
	Grid,
	Paper,
	Tooltip,
	Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LocalShippingOutlinedIcon from '@material-ui/icons/LocalShippingOutlined';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import {
	PAYMENT_HOST_CODES,
	PAYMENT_TYPE_CODES,
	TRANSACTION_STATUS_CODES,
} from '../constants/transactionCodes.js';
import { readableDate } from '../utils/dateTime.js';
import { displayHost, displayPaymentType, displayStatus } from '../utils/transactionDisplay.js';
import { copyText, displayValue, hasValue, shortenSensitiveValues } from '../utils/displayUtils.js';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import useStyles from './TransactionDetailsModal.styles.jsx';

function DetailField({ label, value, copyable = false, status = false, statusClass, statusColor = 'default', classes, onCopy }) {
	if (!hasValue(value)) return null;
	const text = displayValue(value);
	return (
		<div className={classes.field}>
			<Typography className={classes.fieldLabel}>{label}</Typography>
			<div className={classes.fieldValue}>
				{status
					? <Chip size="small" label={text} color={statusColor} className={`${classes.badge} ${statusClass}`} />
					: <Typography className={classes.fieldText} title={text}>{text}</Typography>}
				{copyable && <Tooltip title="Copy value"><IconButton className={classes.copyButton} size="small" aria-label={`Copy ${label}`} onClick={() => copyText(text, onCopy)}><FileCopyOutlinedIcon fontSize="small" /></IconButton></Tooltip>}
			</div>
		</div>
	);
}

function TechnicalPanel({ title, source, classes, testId }) {
	const [expanded, setExpanded] = useState(true);
	const [mode, setMode] = useState('pretty');
	const [showFullTokens, setShowFullTokens] = useState(false);
	const [copied, setCopied] = useState(false);
	const sourceText = typeof source === 'string' ? source : (hasValue(source) ? JSON.stringify(source) : '');
	let parsed;
	let validJson = false;
	try {
		parsed = JSON.parse(sourceText);
		validJson = true;
	} catch (error) {
		parsed = sourceText;
	}
	const safe = validJson ? shortenSensitiveValues(parsed, showFullTokens) : { value: sourceText, foundLongValue: false };
	const prettyText = validJson ? JSON.stringify(safe.value, null, 2) : sourceText;
	const visibleText = mode === 'pretty' ? prettyText : sourceText;
	const copyFeedback = () => {
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	};

	return (
		<Accordion className={classes.accordion} expanded={expanded} onChange={(event, isExpanded) => setExpanded(isExpanded)}>
			<AccordionSummary expandIcon={<ExpandMoreIcon />} classes={{ root: classes.accordionSummary, expanded: classes.accordionExpanded }}>
				<Typography variant="subtitle2">{title}</Typography>
				{expanded && <div className={classes.panelTools} onClick={event => event.stopPropagation()}>
					<ButtonGroup size="small" aria-label={`${title} display mode`}>
						<Button className={mode === 'pretty' ? classes.corporateButtonContained : classes.corporateButton} variant={mode === 'pretty' ? 'contained' : 'outlined'} onClick={() => setMode('pretty')}>Pretty</Button>
						<Button className={mode === 'raw' ? classes.corporateButtonContained : classes.corporateButton} variant={mode === 'raw' ? 'contained' : 'outlined'} onClick={() => setMode('raw')}>Raw</Button>
					</ButtonGroup>
					<Tooltip title={copied ? 'Copied' : 'Copy payload'}>
						<IconButton size="small" aria-label={`Copy ${title}`} onClick={() => copyText(sourceText, copyFeedback)}><FileCopyOutlinedIcon fontSize="small" /></IconButton>
					</Tooltip>
				</div>}
			</AccordionSummary>
			<AccordionDetails className={classes.accordionDetails}>
				<div className={classes.panelContent}>
					{!sourceText
						? <Typography color="textSecondary">No {title.toLowerCase()} available.</Typography>
						: <pre className={classes.code} data-testid={testId}>{visibleText}</pre>}
					{mode === 'pretty' && safe.foundLongValue &&
						<Button size="small" onClick={() => setShowFullTokens(!showFullTokens)}>
							{showFullTokens ? 'Hide full tokens' : 'Show full tokens'}
						</Button>}
				</div>
			</AccordionDetails>
		</Accordion>
	);
}

export default function TransactionDetailsModal({ transaction, onClose, darkMode = false }) {
	const classes = useStyles();
	const [copied, setCopied] = useState(false);

	if (!transaction || !transaction.txnID) {
		return null;
	}

	const transactionId = String(transaction.txnID);
	const {
		status: statusCode = TRANSACTION_STATUS_CODES.UNINITIALIZED,
		payHost: paymentHostCode = PAYMENT_HOST_CODES.UNINITIALIZED,
		payType: paymentTypeCode = PAYMENT_TYPE_CODES.PT_NOT_FOUND,
	} = transaction;
	const statusLabel = displayStatus(statusCode);
	const hasAuthorizationDetails = hasValue(transaction.authNumber) || hasValue(transaction.controlNumber) || hasValue(transaction.hostMessage);
	const paymentHost = displayHost(paymentHostCode);
	const paymentType = displayPaymentType(paymentTypeCode);
	const hostBadgeLabel = paymentHostCode === PAYMENT_HOST_CODES.JPM
		? 'JPM (HOST_JPM)'
		: paymentHost;
	const registered = readableDate(transaction.creationTime);
	const amount = Number(transaction.amount);
	const formattedAmount = Number.isFinite(amount)
		? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount / 100)
		: displayValue(transaction.amount);
	const copiedFeedback = () => {
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1600);
	};

	return (
		<Dialog
			open={Boolean(transaction)}
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			PaperProps={{ className: `${classes.dialogPaper} ${darkMode ? classes.darkDialogPaper : ''}` }}
			aria-labelledby="transaction-details-title"
		>
			<header className={classes.header}>
				<div className={classes.headerTop}>
					<div className={classes.headerIdentity}>
						<div className={classes.headerTitleRow}>
							<Typography id="transaction-details-title" className={classes.headerTitle}>
								Transaction #{transactionId}
							</Typography>
							<Tooltip title={copied ? 'Copied' : 'Copy transaction ID'}>
								<IconButton className={classes.copyButton} size="small" aria-label="Copy transaction ID" onClick={() => copyText(transactionId, copiedFeedback)}>
									<FileCopyOutlinedIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</div>
						<div className={classes.badges}>
							<Chip size="small" label={statusLabel} className={`${classes.badge} ${classes[statusLabel] || classes.UNKNOWN_STATUS}`} />
							<Chip size="small" label={hostBadgeLabel} className={`${classes.badge} ${classes.corporateHostBadge}`} />
							<Chip size="small" label={transaction.apexNotified ? 'Apex notified' : 'Apex not notified'} className={`${classes.badge} ${transaction.apexNotified ? classes.statusSuccess : classes.apexNotNotified}`} />
							{copied && <Typography variant="caption" className={classes.corporateFeedback}>Copied</Typography>}
						</div>
					</div>
					<div className={classes.headerSummary}>
						<Typography className={classes.amount}>{formattedAmount}</Typography>
						<Typography variant="subtitle2">Site {transaction.subDept}</Typography>
						{registered && <Typography variant="body2" color="textSecondary">Registered {registered}</Typography>}
					</div>
					<Tooltip title="Close transaction details">
						<IconButton className={classes.headerClose} aria-label="Close transaction details" onClick={onClose} size="small"><CloseIcon /></IconButton>
					</Tooltip>
				</div>
			</header>

			<DialogContent className={classes.content}>
				<div className={classes.cards}>
					<Paper variant="outlined" className={classes.card}>
						<div className={classes.cardHeader}><DescriptionOutlinedIcon fontSize="small" />Transaction Information</div>
						<div className={classes.fieldGrid}>
							<DetailField label="Transaction ID" value={transactionId} copyable classes={classes} onCopy={copiedFeedback} />
							<DetailField label="Amount" value={formattedAmount} classes={classes} />
							<DetailField label="Site / SubDept" value={`${transaction.subDept}${transaction.subDeptName ? ` (${transaction.subDeptName})` : ''}`} classes={classes} />
							<DetailField label="Transaction Date" value={readableDate(transaction.date, false)} classes={classes} />
							<DetailField label="Status" value={statusLabel} status statusClass={classes[statusLabel] || classes.UNKNOWN_STATUS} classes={classes} />
							<DetailField label="Status Code" value={statusCode} classes={classes} />
							<DetailField label="Registered" value={registered} classes={classes} />
							<DetailField label="Apex Notified" value={transaction.apexNotified ? 'Yes' : 'No'} status statusColor={transaction.apexNotified ? 'primary' : 'default'} classes={classes} />
							<DetailField label="Payment Host" value={paymentHost} classes={classes} />
							<DetailField label="Payment Host Code" value={paymentHostCode} classes={classes} />
							<DetailField label="Payment Type" value={paymentType} classes={classes} />
							<DetailField label="Payment Type Code" value={paymentTypeCode} classes={classes} />
							<DetailField label="Payment Media" value={transaction.paymentMedia} classes={classes} />
						</div>
					</Paper>

					<Paper variant="outlined" className={classes.card}>
						<div className={classes.cardHeader}><LocalShippingOutlinedIcon fontSize="small" />Load / Operations</div>
						<div className={classes.operationsGrid}>
							<DetailField label="PO Number" value={transaction.po_numbers} copyable classes={classes} onCopy={copiedFeedback} />
							<DetailField label="Truck ID" value={transaction.truckID} copyable classes={classes} onCopy={copiedFeedback} />
							<DetailField label="Vendor" value={transaction.vendor} classes={classes} />
							<DetailField label="Carrier" value={transaction.carrier} classes={classes} />
							<DetailField label="Driver Phone" value={transaction.driverPhone} copyable classes={classes} onCopy={copiedFeedback} />
							<DetailField label="Door" value={transaction.door} classes={classes} />
							<DetailField label="Odometer" value={transaction.odometer} classes={classes} />
							<DetailField label="Express Code" value={transaction.expressCode} classes={classes} />
							<DetailField label="WP Token" value={transaction.wpToken} copyable classes={classes} onCopy={copiedFeedback} />
						</div>
					</Paper>
				</div>

				{hasAuthorizationDetails && <Paper variant="outlined" className={`${classes.card} ${classes.technicalSection}`}>
					<div className={classes.cardHeader}><InfoOutlinedIcon fontSize="small" />Authorization</div>
					<div className={classes.fieldGrid}>
						<DetailField label="Auth Number" value={transaction.authNumber} copyable classes={classes} onCopy={copiedFeedback} />
						<DetailField label="Control Number" value={transaction.controlNumber} copyable classes={classes} onCopy={copiedFeedback} />
						<DetailField label="Host Message" value={transaction.hostMessage} classes={classes} />
					</div>
				</Paper>}

				<div className={classes.technicalSection}>
					<Typography className={classes.cardHeader}><SwapHorizIcon fontSize="small" />Host Exchange</Typography>
					<div className={classes.exchangeGrid}>
						<TechnicalPanel title="Host Request" source={transaction.hostRequest} classes={classes} testId="host-request-payload" />
						<TechnicalPanel title="Host Response" source={transaction.hostResponse} classes={classes} testId="host-response-payload" />
					</div>
				</div>
			</DialogContent>
			<DialogActions className={classes.footer}>
				<Button className={classes.corporateButton} onClick={onClose} variant="outlined">Close</Button>
			</DialogActions>
		</Dialog>
	);
}
